import React, { useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { GET_MEDIA_APPROVAL_BY_ID, GetMediaApprovalByIdData, GetMediaApprovalByIdVariables } from '@jwp/ott-common/src/graphql/query/moderate.queries';
import env from '@jwp/ott-common/src/env';
import { formatDurationTag } from '@jwp/ott-common/src/utils/formatting';
import { PATH_VIDEO_PREVIEW } from '@jwp/ott-common/src/paths';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';

import VideoDetails from '../../components/VideoDetails/VideoDetails';
import VideoMetaData from '../../components/VideoMetaData/VideoMetaData';
import StartWatchingButton from '../../containers/StartWatchingButton/StartWatchingButton';
import FavoriteButton from '../../containers/FavoriteButton/FavoriteButton';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';
import { useTheme } from '../../../../../ThemeContext';

import styles from './MyVideoDetailsPage.module.scss';

const MyVideoDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation(['video', 'common', 'upload']);
  const { universalStyles } = useTheme();
  const location = useLocation();
  const user = useAccountStore((state) => state.user);
  const isLoggedIn = !!user;

  const { data, loading, error } = useQuery<GetMediaApprovalByIdData, GetMediaApprovalByIdVariables>(GET_MEDIA_APPROVAL_BY_ID, {
    variables: { maId: id || '' },
    skip: !id,
  });

  const videoData = data?.mediaApprovalByMaId;

  const primaryMetadata = useMemo(() => {
    if (!videoData) return null;
    const items = [];
    if (videoData.maDuration) items.push(formatDurationTag(Number(videoData.maDuration)));
    if (videoData.maCreatedAt) items.push(new Date(videoData.maCreatedAt).getFullYear().toString());
    if(videoData.maType === 'VIDEOS') items.push(t(`upload:video`));
    if(videoData.maType === 'REELS') items.push(t(`upload:reel`));
    // items.push('English'); // placeholder for language if not in data
    if (videoData.dropdownByAgeRatingId?.ddLabel) items.push(videoData.dropdownByAgeRatingId.ddLabel);
    
    return <VideoMetaData attributes={items} style={universalStyles.secondaryText} />;
  }, [videoData, t, universalStyles]);

  const genres = useMemo(() => {
    return videoData?.genreDropdownsByMaId?.nodes?.map((n: any) => n.dropdownByDdId.ddLabel) || [];
  }, [videoData]);

  if (loading) return <LoadingOverlay />;
  if (error || !videoData) return <div>Error loading video details</div>;

  const backgroundImage = `${env.APP_BASE_URL || ''}${videoData.maS3ThumbnailKeyUrl}`;
  const playUrl = `${PATH_VIDEO_PREVIEW}/${videoData.maId}?returnPath=${encodeURIComponent(location.pathname + location.search)}`;
  const isApproved = videoData.maStatus === 'APPROVED';

  // PlaylistItem
  const item = {
    mediaid: isApproved ? videoData.maJwMediaId : videoData.maId,
    title: videoData.maTitle,
    description: videoData.maDescription,
    image: backgroundImage,
    duration: Number(videoData.maDuration),
    category: videoData.maCategory,
    tags: videoData.maTags,
    genre: genres.join(','),
    free: 'true', // Mark as free to allow StartWatchingButton to function
  };

  return (
    <div className={styles.pageWrapper}>
      <VideoDetails
        title={videoData.maTitle}
        description={videoData.maDescription}
        image={backgroundImage}
        primaryMetadata={primaryMetadata}
        movieGenreMetadata={genres}
        item={item as any}
        startWatchingButton={
          <StartWatchingButton
            item={item as any}
            playUrl={playUrl}
          />
        }
        favoriteButton={isApproved && isLoggedIn ? <FavoriteButton item={item as any} /> : null}
        shareButton={null}
      >
        <div />
      </VideoDetails>
    </div>
  );
};

export default MyVideoDetailsPage;
